import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  User, 
  ChevronRight, 
  BookOpen, 
  Tag, 
  ArrowLeft, 
  AlertTriangle,
  Heart,
  TrendingUp,
  Users,
  Sparkles,
  FileText,
  Filter,
  MapPin,
  Building,
  X,
  ChevronDown,
  Stethoscope
} from '../lib/icons';
import { useSearch } from '../hooks/useSearch';
import { useSpecialties } from '../hooks/useSpecialties';
import { useTopicsList } from '../hooks/useTopics';
import { createUniqueSlug, createDoctorSlug } from '../utils/slugUtils';
import DoctorCard from './DoctorCard';
import PaginationControls from './PaginationControls';
import { getProviderTypeLabel } from '../utils/providerTypeUtils';
import BlogCard from './BlogCard';

const SearchResults: React.FC = () => {
  const { searchTerm, searchResults, loading, error, performSearch } = useSearch();
  const { specialties } = useSpecialties();
  // useTopicsList() — Listen-Felder ohne content.
  const { topics } = useTopicsList();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<'all' | 'doctors' | 'blog' | 'topics' | 'specialties'>('all');
  const [showNewSearch, setShowNewSearch] = useState(false);
  const [doctorsPage, setDoctorsPage] = useState(1);
  const [blogPage, setBlogPage] = useState(1);
  
  // Provider filters - changed to arrays for multi-select
  const [showProviderFilters, setShowProviderFilters] = useState(false);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState<string[]>([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);
  
  // Search terms for filter dropdowns
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [specialtySearchTerm, setSpecialtySearchTerm] = useState('');
  const [topicSearchTerm, setTopicSearchTerm] = useState('');
  
  // Dropdown visibility states
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  
  const itemsPerPage = 10;

  // Get unique cities from search results
  const availableCities = React.useMemo(() => {
    const cities = Array.from(new Set(searchResults.doctors.map(doctor => doctor.city)))
      .filter(city => city)
      .sort();
    return cities;
  }, [searchResults.doctors]);

  // Provider types
  const providerTypes = [
    { value: 'doctor', label: 'Arzt / Ärztin' },
    { value: 'group_practice', label: 'Gemeinschaftspraxis' },
    { value: 'clinic', label: 'Klinik / Krankenhaus' },
    { value: 'naturopath', label: 'Heilpraktiker / Heilpraktikerin' },
    { value: 'psychotherapist', label: 'Psychotherapeut / Psychotherapeutin' },
    { value: 'therapy_practice', label: 'Therapeutische Praxis' },
    { value: 'ambulanz', label: 'Ambulantes OP-Zentrum / Tagesklinik' },
    { value: 'fachklinik', label: 'Fachklinik / Spezialklinik' },
    { value: 'labor_diagnostik', label: 'Labor / Diagnostikzentrum' },
    { value: 'mvz', label: 'MVZ (Medizinisches Versorgungszentrum)' },
    { value: 'naturheilpraxis', label: 'Naturheilpraxis / Komplementärmedizinisches Zentrum' },
    { value: 'pflegedienst', label: 'Pflegedienst / Pflegeeinrichtung' },
    { value: 'reha', label: 'Rehaklinik / Rehabilitationszentrum' },
    { value: 'dentist', label: 'Zahnärztliche Einrichtung' },
    { value: 'sonstiges', label: 'Sonstiges' }
  ];

  // Filter functions for search
  const filteredCities = availableCities.filter(city =>
    city.toLowerCase().includes(citySearchTerm.toLowerCase())
  );

  const filteredTypes = providerTypes.filter(type =>
    type.label.toLowerCase().includes(typeSearchTerm.toLowerCase())
  );

  const filteredSpecialties = specialties.filter(specialty =>
    specialty.name.toLowerCase().includes(specialtySearchTerm.toLowerCase())
  );

  const filteredTopics = topics.filter(topic =>
    topic.name.toLowerCase().includes(topicSearchTerm.toLowerCase())
  );

  // Toggle functions for multi-select
  const handleCityToggle = (city: string) => {
    if (selectedCities.includes(city)) {
      setSelectedCities(selectedCities.filter(c => c !== city));
    } else {
      setSelectedCities([...selectedCities, city]);
    }
  };

  const handleTypeToggle = (type: string) => {
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter(t => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
  };

  const handleSpecialtyToggle = (specialtyId: string) => {
    if (selectedSpecialtyIds.includes(specialtyId)) {
      setSelectedSpecialtyIds(selectedSpecialtyIds.filter(id => id !== specialtyId));
    } else {
      setSelectedSpecialtyIds([...selectedSpecialtyIds, specialtyId]);
    }
  };

  const handleTopicToggle = (topicId: string) => {
    if (selectedTopicIds.includes(topicId)) {
      setSelectedTopicIds(selectedTopicIds.filter(id => id !== topicId));
    } else {
      setSelectedTopicIds([...selectedTopicIds, topicId]);
    }
  };

  // Helper functions to get selected names
  const getSelectedTypeLabels = () => {
    return selectedTypes.map(type => 
      providerTypes.find(pt => pt.value === type)?.label || type
    );
  };

  const getSelectedSpecialtyNames = () => {
    return selectedSpecialtyIds.map(id => 
      specialties.find(s => s.id === id)?.name || 'Unbekannt'
    );
  };

  const getSelectedTopicNames = () => {
    return selectedTopicIds.map(id => 
      topics.find(t => t.id === id)?.name || 'Unbekannt'
    );
  };

  // Sort doctors by premium plan first
  const sortedDoctors = React.useMemo(() => {
    return [...searchResults.doctors].sort((a, b) => {
      // First priority: Premium plan only
      if (a.plan === 'premium' && b.plan !== 'premium') {
        return -1; // a comes first
      }
      if (a.plan !== 'premium' && b.plan === 'premium') {
        return 1; // b comes first
      }
      
      // Second priority: Creation date (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [searchResults.doctors]);

  // Apply provider filters
  const filteredDoctors = React.useMemo(() => {
    let filtered = [...sortedDoctors];
    
    if (selectedCities.length > 0) {
      filtered = filtered.filter(doctor => selectedCities.includes(doctor.city));
    }
    
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(doctor => selectedTypes.includes(doctor.type));
    }
    
    if (selectedSpecialtyIds.length > 0) {
      // Note: This would need doctor specialties data to work properly
      // For now, we'll keep the filter but it won't actually filter
    }
    
    if (selectedTopicIds.length > 0) {
      // Note: This would need doctor topics data to work properly
      // For now, we'll keep the filter but it won't actually filter
    }
    
    return filtered;
  }, [sortedDoctors, selectedCities, selectedTypes, selectedSpecialtyIds, selectedTopicIds]);

  // Reset pagination when search results change
  React.useEffect(() => {
    setDoctorsPage(1);
    setBlogPage(1);
  }, [searchResults]);

  // Reset pagination when filter changes
  React.useEffect(() => {
    setDoctorsPage(1);
    setBlogPage(1);
  }, [activeFilter, selectedCities, selectedTypes, selectedSpecialtyIds, selectedTopicIds]);

  // Clear all provider filters
  const clearProviderFilters = () => {
    setSelectedCities([]);
    setSelectedTypes([]);
    setSelectedSpecialtyIds([]);
    setSelectedTopicIds([]);
    setCitySearchTerm('');
    setTypeSearchTerm('');
    setSpecialtySearchTerm('');
    setTopicSearchTerm('');
    setShowCityDropdown(false);
    setShowTypeDropdown(false);
    setShowSpecialtyDropdown(false);
    setShowTopicDropdown(false);
  };

  // Check if any provider filters are active
  const hasActiveProviderFilters = selectedCities.length > 0 || selectedTypes.length > 0 || selectedSpecialtyIds.length > 0 || selectedTopicIds.length > 0;

  const handleDoctorClick = (doctorId: string) => {
    const d = searchResults.doctors.find((x: any) => x.id === doctorId);
    const slug = d ? createDoctorSlug(d) : doctorId;
    navigate(`/anbieter/${slug}`, { state: { fromSearch: true } });
  };

  const handleBlogPostClick = (postId: string) => {
    const post = searchResults.blogPosts.find(p => p.id === postId);
    if (post) {
      const slug = createUniqueSlug(post.title, post.id);
      navigate(`/wissen/${slug}`, { state: { fromSearch: true } });
    }
  };

  const handleTopicClick = (topic: any) => {
    const slug = createUniqueSlug(topic.name, topic.id);
    const topicType = topic.topic_type || 'consultation';
    
    // Pass search state for back navigation
    const navigationState = { fromSearch: true };
    
    switch (topicType) {
      case 'disease':
        navigate(`/krankheiten/${slug}`, { state: navigationState });
        break;
      case 'symptom':
        navigate(`/symptome/${slug}`, { state: navigationState });
        break;
      case 'consultation':
      default:
        navigate(`/beratung/${slug}`, { state: navigationState });
        break;
    }
  };

  const handleSpecialtyClick = (specialty: any) => {
    // Navigate to doctors page with specialty filter
    navigate('/anbieter', { state: { specialtyFilter: specialty.name, fromSearch: true } });
  };

  const getTopicIcon = (topicType: string) => {
    switch (topicType) {
      case 'disease':
        return AlertTriangle;
      case 'symptom':
        return AlertTriangle;
      case 'consultation':
      default:
        return Heart;
    }
  };

  const getTopicColor = (topicType: string) => {
    switch (topicType) {
      case 'disease':
        return 'bg-red-100 text-red-800';
      case 'symptom':
        return 'bg-yellow-100 text-yellow-800';
      case 'consultation':
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getTopicTypeLabel = (topicType: string) => {
    switch (topicType) {
      case 'disease':
        return 'Krankheit';
      case 'symptom':
        return 'Symptom';
      case 'consultation':
      default:
        return 'Beratung';
    }
  };

  // Calculate total results
  const totalResults = searchResults.doctors.length + 
                      searchResults.blogPosts.length + 
                      searchResults.topics.length;

  // Filter results based on active filter
  const getFilteredResults = () => {
    switch (activeFilter) {
      case 'doctors':
        return { doctors: filteredDoctors, blogPosts: [], topics: [] };
      case 'blog':
        return { doctors: [], blogPosts: searchResults.blogPosts, topics: [] };
      case 'topics':
        return { doctors: [], blogPosts: [], topics: searchResults.topics };
      default:
        return { ...searchResults, doctors: filteredDoctors };
    }
  };

  const filteredResults = getFilteredResults();

  // Pagination for doctors
  const doctorsTotalPages = Math.ceil(filteredResults.doctors.length / itemsPerPage);
  const doctorsStartIndex = (doctorsPage - 1) * itemsPerPage;
  const paginatedDoctors = filteredResults.doctors.slice(doctorsStartIndex, doctorsStartIndex + itemsPerPage);

  // Pagination for blog posts
  const blogTotalPages = Math.ceil(filteredResults.blogPosts.length / itemsPerPage);
  const blogStartIndex = (blogPage - 1) * itemsPerPage;
  const paginatedBlogPosts = filteredResults.blogPosts.slice(blogStartIndex, blogStartIndex + itemsPerPage);
  
  if (!searchTerm) {
    return (
      <section className="py-16 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Keine Suchanfrage</h2>
            <p className="text-gray-600">Verwenden Sie die Suchleiste, um nach Inhalten zu suchen.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 bg-gray-50 min-h-screen relative overflow-hidden">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 relative">
          <button
            onClick={() => navigate('/')}
            className="flex items-center px-3 py-1.5 text-gray-600 hover:text-gray-900 transition-colors text-sm mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Zurück
          </button>

          <div className="text-center mb-6">
            <div className="inline-flex items-center px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200 mb-3">
              <Search className="w-4 h-4 text-blue-600 mr-2" />
              <span className="text-blue-700 font-medium text-sm">Suchergebnisse</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight">
              Ergebnisse für <span className="text-blue-600">"{searchTerm}"</span>
            </h1>
          </div>
          
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowNewSearch(!showNewSearch)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Search className="w-4 h-4 mr-2" />
              Neue Suche
            </button>
          </div>
          
          {/* Collapsible Search Form */}
          {showNewSearch && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const newSearchTerm = formData.get('search') as string;
                if (newSearchTerm.trim()) {
                  performSearch(newSearchTerm.trim());
                  setShowNewSearch(false);
                }
              }} className="flex gap-3">
                <div className="flex-1 min-w-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      name="search"
                      placeholder="Neue Suche starten..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 text-sm font-medium whitespace-nowrap shadow-lg"
                >
                  Suchen
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewSearch(false)}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
                >
                  ✕
                </button>
              </form>
            </div>
          )}
          
          {!loading && (
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 bg-white rounded-lg border border-gray-300">
                <TrendingUp className="w-4 h-4 text-gray-600 mr-2" />
                <span className="text-gray-700 font-medium text-sm">
                  {totalResults} {totalResults === 1 ? 'Ergebnis gefunden' : 'Ergebnisse gefunden'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Durchsuche alle Inhalte...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50/80 backdrop-blur-sm border border-red-200/50 rounded-2xl p-6 mb-8 shadow-lg">
            <p className="text-red-600">Fehler bei der Suche: {error}</p>
          </div>
        )}

        {/* Filter Tabs */}
        {!loading && totalResults > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setActiveFilter('all')}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                activeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              <div className="flex items-center gap-2">
                <span className="font-medium">Alle</span>
                <span className={`text-sm ${activeFilter === 'all' ? 'text-white/80' : 'text-gray-500'}`}>
                  {totalResults}
                </span>
              </div>
            </button>
            {searchResults.doctors.length > 0 && (
              <button
                onClick={() => setActiveFilter('doctors')}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeFilter === 'doctors'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <User className="w-4 h-4 mr-2" />
                <div className="flex items-center gap-2">
                  <span className="font-medium">Anbieter</span>
                  <span className={`text-sm ${activeFilter === 'doctors' ? 'text-white/80' : 'text-gray-500'}`}>
                    {searchResults.doctors.length}
                  </span>
                </div>
              </button>
            )}
            {searchResults.topics.length > 0 && (
              <button
                onClick={() => setActiveFilter('topics')}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeFilter === 'topics'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Tag className="w-4 h-4 mr-2" />
                <div className="flex items-center gap-2">
                  <span className="font-medium">Themen</span>
                  <span className={`text-sm ${activeFilter === 'topics' ? 'text-white/80' : 'text-gray-500'}`}>
                    {searchResults.topics.length}
                  </span>
                </div>
              </button>
            )}
            {searchResults.blogPosts.length > 0 && (
              <button
                onClick={() => setActiveFilter('blog')}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeFilter === 'blog'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                <div className="flex items-center gap-2">
                  <span className="font-medium">Artikel</span>
                  <span className={`text-sm ${activeFilter === 'blog' ? 'text-white/80' : 'text-gray-500'}`}>
                    {searchResults.blogPosts.length}
                  </span>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Topics Results */}
        {!loading && filteredResults.topics.length > 0 && (
          <div className="mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <Tag className="w-5 h-5 text-blue-600" />
              </div>
              Gesundheitsthemen ({filteredResults.topics.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResults.topics.map((topic) => {
                const topicType = (topic as any).topic_type || 'consultation';
                const IconComponent = getTopicIcon(topicType);
                const gradientClass = topicType === 'disease' 
                  ? 'from-red-500 to-pink-600'
                  : topicType === 'symptom'
                  ? 'from-amber-500 to-orange-600'
                  : 'from-emerald-500 to-teal-600';
                
                return (
                  <div
                    key={topic.id}
                    className="group bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all cursor-pointer overflow-hidden"
                    onClick={() => handleTopicClick(topic)}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                            {getTopicTypeLabel(topicType)}
                          </span>
                        </div>
                      </div>

                      <h4 className="text-base font-semibold text-gray-900 mb-2 leading-tight group-hover:text-blue-600 transition-colors line-clamp-2">
                        {topic.name}
                      </h4>

                      {topic.description && (
                        <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                          {topic.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Doctors Results */}
        {!loading && filteredResults.doctors.length > 0 && (
          <div className="mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              Gesundheitsanbieter ({filteredResults.doctors.length})
            </h3>
            
            {/* Provider Filter Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setShowProviderFilters(!showProviderFilters)}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    showProviderFilters || hasActiveProviderFilters
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                  {hasActiveProviderFilters && (
                    <span className="ml-2 bg-white/20 text-xs px-2 py-0.5 rounded">
                      Aktiv
                    </span>
                  )}
                </button>
              </div>
              
              {/* Filter Panel */}
              {showProviderFilters && (
                <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Type Filter with Multi-Select */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                        className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <Building className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-gray-700">
                            {selectedTypes.length === 0 
                              ? 'Anbietertyp wählen...'
                              : selectedTypes.length === 1
                              ? getSelectedTypeLabels()[0]
                              : `${selectedTypes.length} Typen ausgewählt`
                            }
                          </span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                      
                      {showTypeDropdown && (
                        <>
                          <div
                            className="fixed inset-0 z-5"
                            onClick={() => setShowTypeDropdown(false)}
                          />
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {/* Search Input */}
                            <div className="p-3 border-b border-gray-200">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                  type="text"
                                  value={typeSearchTerm}
                                  onChange={(e) => setTypeSearchTerm(e.target.value)}
                                  placeholder="Anbietertyp suchen..."
                                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            
                            <div className="max-h-48 overflow-y-auto">
                              {filteredTypes.length > 0 ? (
                                filteredTypes.map(type => (
                                  <label
                                    key={type.value}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedTypes.includes(type.value)}
                                      onChange={() => handleTypeToggle(type.value)}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                                    />
                                    <Building className="w-4 h-4 mr-2" />
                                    {type.label}
                                  </label>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                  Keine Anbietertypen gefunden
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Specialty Filter with Multi-Select */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowSpecialtyDropdown(!showSpecialtyDropdown)}
                        className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <Stethoscope className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-gray-700">
                            {selectedSpecialtyIds.length === 0 
                              ? 'Fachgebiet wählen...'
                              : selectedSpecialtyIds.length === 1
                              ? getSelectedSpecialtyNames()[0]
                              : `${selectedSpecialtyIds.length} Fachgebiete ausgewählt`
                            }
                          </span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                      
                      {showSpecialtyDropdown && (
                        <>
                          <div
                            className="fixed inset-0 z-5"
                            onClick={() => setShowSpecialtyDropdown(false)}
                          />
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                            {/* Search Input */}
                            <div className="p-3 border-b border-gray-200">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                  type="text"
                                  value={specialtySearchTerm}
                                  onChange={(e) => setSpecialtySearchTerm(e.target.value)}
                                  placeholder="Fachgebiet suchen..."
                                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            
                            <div className="max-h-48 overflow-y-auto">
                              {filteredSpecialties.length > 0 ? (
                                filteredSpecialties.map(specialty => (
                                  <label
                                    key={specialty.id}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedSpecialtyIds.includes(specialty.id)}
                                      onChange={() => handleSpecialtyToggle(specialty.id)}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                                    />
                                    {specialty.name}
                                  </label>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                  Keine Fachgebiete gefunden
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Topic Filter with Multi-Select */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowTopicDropdown(!showTopicDropdown)}
                        className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <Heart className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-gray-700">
                            {selectedTopicIds.length === 0 
                              ? 'Thema wählen...'
                              : selectedTopicIds.length === 1
                              ? getSelectedTopicNames()[0]
                              : `${selectedTopicIds.length} Themen ausgewählt`
                            }
                          </span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                      
                      {showTopicDropdown && (
                        <>
                          <div
                            className="fixed inset-0 z-5"
                            onClick={() => setShowTopicDropdown(false)}
                          />
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                            {/* Search Input */}
                            <div className="p-3 border-b border-gray-200">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                  type="text"
                                  value={topicSearchTerm}
                                  onChange={(e) => setTopicSearchTerm(e.target.value)}
                                  placeholder="Behandlungsthema suchen..."
                                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            
                            <div className="max-h-48 overflow-y-auto">
                              {filteredTopics.length > 0 ? (
                                filteredTopics.filter(topic => topic.is_active).map(topic => (
                                  <label
                                    key={topic.id}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedTopicIds.includes(topic.id)}
                                      onChange={() => handleTopicToggle(topic.id)}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                                    />
                                    {topic.name}
                                  </label>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                  Keine Behandlungsthemen gefunden
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* City Filter */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowCityDropdown(!showCityDropdown)}
                        className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <MapPin className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-gray-700">
                            {selectedCities.length === 0 
                              ? 'Stadt wählen...'
                              : selectedCities.length === 1
                              ? selectedCities[0]
                              : `${selectedCities.length} Städte ausgewählt`
                            }
                          </span>
                        </div>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                      
                      {showCityDropdown && (
                        <>
                          <div
                            className="fixed inset-0 z-5"
                            onClick={() => setShowCityDropdown(false)}
                          />
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                            {/* Search Input */}
                            <div className="p-3 border-b border-gray-200">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                  type="text"
                                  value={citySearchTerm}
                                  onChange={(e) => setCitySearchTerm(e.target.value)}
                                  placeholder="Stadt suchen..."
                                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            
                            <div className="max-h-48 overflow-y-auto">
                              {filteredCities.length > 0 ? (
                                filteredCities.map(city => (
                                  <label
                                    key={city}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedCities.includes(city)}
                                      onChange={() => handleCityToggle(city)}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                                    />
                                    {city}
                                  </label>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                  Keine Städte gefunden
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Selected Filters Display */}
                  {hasActiveProviderFilters && (
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                      {/* Selected Types */}
                      {selectedTypes.map(type => {
                        const typeData = providerTypes.find(pt => pt.value === type);
                        return (
                          <span
                            key={type}
                            className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            <Building className="w-3 h-3 mr-1" />
                            {typeData?.label}
                            <button
                              onClick={() => handleTypeToggle(type)}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}

                      {/* Selected Specialties */}
                      {selectedSpecialtyIds.map(specialtyId => {
                        const specialty = specialties.find(s => s.id === specialtyId);
                        return (
                          <span
                            key={specialtyId}
                            className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                          >
                            <Stethoscope className="w-3 h-3 mr-1" />
                            {specialty?.name}
                            <button
                              onClick={() => handleSpecialtyToggle(specialtyId)}
                              className="ml-2 text-green-600 hover:text-green-800"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}

                      {/* Selected Topics */}
                      {selectedTopicIds.map(topicId => {
                        const topic = topics.find(t => t.id === topicId);
                        return (
                          <span
                            key={topicId}
                            className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                          >
                            <Heart className="w-3 h-3 mr-1" />
                            {topic?.name}
                            <button
                              onClick={() => handleTopicToggle(topicId)}
                              className="ml-2 text-purple-600 hover:text-purple-800"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}

                      {/* Selected Cities */}
                      {selectedCities.map(city => (
                        <span
                          key={city}
                          className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm"
                        >
                          <MapPin className="w-3 h-3 mr-1" />
                          {city}
                          <button
                            onClick={() => handleCityToggle(city)}
                            className="ml-2 text-orange-600 hover:text-orange-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Filter Actions */}
                  {hasActiveProviderFilters && (
                    <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-600">
                        {filteredResults.doctors.length} von {sortedDoctors.length} Anbietern gefunden
                      </div>
                      <button
                        onClick={clearProviderFilters}
                        className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Filter zurücksetzen
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Active Filters Display */}
              {hasActiveProviderFilters && !showProviderFilters && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedCities.map(city => (
                    <span
                      key={city}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      {city}
                      <button
                        onClick={() => handleCityToggle(city)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {selectedTypes.map(type => {
                    const typeData = providerTypes.find(pt => pt.value === type);
                    return (
                      <span
                        key={type}
                        className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        <Building className="w-3 h-3 mr-1" />
                        {typeData?.label}
                        <button
                          onClick={() => handleTypeToggle(type)}
                          className="ml-2 text-green-600 hover:text-green-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                  {selectedSpecialtyIds.map(specialtyId => {
                    const specialty = specialties.find(s => s.id === specialtyId);
                    return (
                      <span
                        key={specialtyId}
                        className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                      >
                        <Stethoscope className="w-3 h-3 mr-1" />
                        {specialty?.name}
                        <button
                          onClick={() => handleSpecialtyToggle(specialtyId)}
                          className="ml-2 text-purple-600 hover:text-purple-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                  {selectedTopicIds.map(topicId => {
                    const topic = topics.find(t => t.id === topicId);
                    return (
                      <span
                        key={topicId}
                        className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm"
                      >
                        <Heart className="w-3 h-3 mr-1" />
                        {topic?.name}
                        <button
                          onClick={() => handleTopicToggle(topicId)}
                          className="ml-2 text-orange-600 hover:text-orange-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Results info */}
            {doctorsTotalPages > 1 && (
              <div className="text-center mb-6">
                <p className="text-gray-600">
                  Zeige {doctorsStartIndex + 1} - {Math.min(doctorsStartIndex + itemsPerPage, filteredResults.doctors.length)} von {filteredResults.doctors.length} Anbietern
                </p>
              </div>
            )}
            
            <div className="space-y-6">
              {paginatedDoctors.map((doctor) => (
                <DoctorCard
                  key={doctor.id}
                  doctor={doctor}
                  onDoctorClick={handleDoctorClick}
                  fromSearch={true}
                />
              ))}
            </div>
            
            {/* Pagination for doctors */}
            <PaginationControls
              currentPage={doctorsPage}
              totalPages={doctorsTotalPages}
              totalItems={filteredResults.doctors.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setDoctorsPage}
              className="mt-8"
            />
          </div>
        )}

        {/* Specialties Results */}

        {/* Blog Posts Results */}
        {!loading && filteredResults.blogPosts.length > 0 && (
          <div className="mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              Wissen & Artikel ({filteredResults.blogPosts.length})
            </h3>
            
            {/* Results info */}
            {blogTotalPages > 1 && (
              <div className="text-center mb-6">
                <p className="text-gray-600">
                  Zeige {blogStartIndex + 1} - {Math.min(blogStartIndex + itemsPerPage, filteredResults.blogPosts.length)} von {filteredResults.blogPosts.length} Artikeln
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedBlogPosts.map((post) => (
                <BlogCard
                  key={post.id}
                  post={post}
                  onClick={handleBlogPostClick}
                  className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 hover:shadow-md transition-all cursor-pointer"
                />
              ))}
            </div>
            
            {/* Pagination for blog posts */}
            <PaginationControls
              currentPage={blogPage}
              totalPages={blogTotalPages}
              totalItems={filteredResults.blogPosts.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setBlogPage}
              className="mt-8"
            />
          </div>
        )}

        {/* No Results */}
        {!loading && totalResults === 0 && searchTerm && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Keine Ergebnisse gefunden
              </h3>
              <p className="text-gray-600 mb-6">
                Für "{searchTerm}" wurden keine passenden Inhalte gefunden.
              </p>
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center justify-center">
                  <Search className="w-4 h-4 mr-2" />
                  Suchtipps:
                </h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></div>
                    Versuchen Sie allgemeinere Begriffe
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></div>
                    Suchen Sie nach Fachrichtungen
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></div>
                    Prüfen Sie die Rechtschreibung
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/anbieter')}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Alle Anbieter durchsuchen
              </button>
              <button
                onClick={() => navigate('/wissen')}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Wissen durchstöbern
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Zur Startseite
              </button>
            </div>
          </div>
        )}

        {/* No Providers Found with Filters */}
        {!loading && filteredResults.doctors.length === 0 && sortedDoctors.length > 0 && hasActiveProviderFilters && (
          <div className="text-center py-12 mb-12">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Filter className="w-12 h-12 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Keine Anbieter mit diesen Filtern
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Für die ausgewählten Filter wurden keine passenden Anbieter gefunden.
              </p>
              <div className="space-y-3">
                <button
                  onClick={clearProviderFilters}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium"
                >
                  Filter zurücksetzen
                </button>
                <p className="text-sm text-gray-500">
                  {sortedDoctors.length} Anbieter ohne Filter verfügbar
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default SearchResults;